import React, { useState, useEffect, useCallback, useRef } from "react";
import Video, { Room } from "twilio-video";
import Participant from "./Participant";
import "./player.css";
import swal from "sweetalert";
import moment from "moment";
import Toolbar from "./Toolbar";
import { Error } from "./Error";
import C2CLogger from "../../Util/Helper/C2CLogger";

const Player = ({
  endDateTime,
  roomName,
  token,
  endSession,
  isAudioCall,
  isVideoCall,
  appointment,
  history,
  displayNetworkText,
  handleOnHold,
}) => {
  const [room, setRoom] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isAudioOpen, setAudio] = useState(true);
  const [isVideoOpen, setVideo] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenShareTrack, setScreenShareTrack] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [participantStatus, setParticipantStatus] = useState(
    "Waiting for your participant to connect..."
  );
  const [participantVideoTrackStatus, setParticipantVideoTrackStatus] =
    useState(true);
  const [participantAudioTrackStatus, setParticipantAudioTrackStatus] =
    useState(true);
  const [roomStatus, setRoomStatus] = useState(true);
  const [selfStatus, setSelfStatus] = useState("Connected");
  const [selfStatusText, setNetworkStatusText] = useState("");
  const [networkStatus, setNetworkStatus] = useState(5);
  const [endMeetingTimer, setEndMeetingTimer] = useState(null);
  const logger = new C2CLogger();
  useEffect(() => {
    const isRoomEmpty =
      room && Object.keys(room).length === 0 && room.constructor === Object;

    const isAudioCallStatus =
      (typeof isAudioCall === "string" && isAudioCall === "false") ||
      (typeof isAudioCall === "boolean" && isAudioCall === false);

    if (isAudioCallStatus && room && !isRoomEmpty) {
      setAudio(isAudioCall);
      muteAudio();
    }
  }, [room, isAudioCall]);

  //muting the audio track
  const muteAudio = useCallback(() => {
    localStorage.setItem("audioStatus", !isAudioOpen);

    if (room) {
      let audioStatus = !isAudioOpen;

      room.localParticipant.audioTracks.forEach((publication) => {
        if (!audioStatus) publication.track.disable();
        else publication.track.enable();
      });
      let status = audioStatus ? "Muted" : "UnMuted";
      setAudio(audioStatus);
    }
  }, [room, isAudioOpen, setAudio]);

  const roomClosed = useCallback(() => {
    setRoomStatus(false);
  }, [setRoomStatus]);

  //muting the video track
  const muteVideo = useCallback(() => {
    localStorage.setItem("videoStatus", !isVideoOpen);
    if (isScreenSharing) {
      swal({
        title: "Can't start Video",
        text: "Switch off screen sharing before turning your video on",
        type: "failed",
      }).then(function () {});
    } else {
      let videoStatus = !isVideoOpen;

      if (room) {
        room.localParticipant.videoTracks.forEach((publication) => {
          if (!videoStatus) publication.track.disable();
          else publication.track.enable();
        });
        let status = videoStatus ? "Muted" : "UnMuted";
        setVideo(videoStatus);
      }
    }
  }, [room, isVideoOpen, isScreenSharing, setVideo]);

  //screen share
  const screenShare = useCallback(() => {
    if (!isScreenSharing) {
      navigator.mediaDevices
        .getDisplayMedia({
          audio: false,
        })
        .then((stream) => {
          const track = stream.getTracks()[0];
          setScreenShareTrack(track);
          room.localParticipant
            .publishTrack(track, {
              name: "screen",
            })
            .then(() => {
              room.localParticipant.videoTracks.forEach((publication) => {
                if (publication.trackName !== "screen") {
                  setLocalVideoTrack(publication.track);
                  publication.track.disable();
                  room.localParticipant.unpublishTrack(publication.track);
                  setVideo(false);
                  setIsScreenSharing(true);
                }
              });
            });
          track.onended = () => {
            closeScreenShare();
          };
        });
    } else {
      if (screenShareTrack && localVideoTrack) {
        screenShareTrack.stop();
        room.localParticipant.publishTrack(localVideoTrack);
        room.localParticipant.unpublishTrack(screenShareTrack);
        setIsScreenSharing(false);
        setScreenShareTrack(null);
      }
    }
  }, [
    room,
    isScreenSharing,
    screenShareTrack,
    localVideoTrack,
    setIsScreenSharing,
    setScreenShareTrack,
    setRoom,
    setLocalVideoTrack,
  ]);

  //close screen share by clicking the stop button explicitly
  const closeScreenShare = useCallback(() => {
    if (localVideoTrack) {
      room.localParticipant.publishTrack(localVideoTrack);
      setScreenShareTrack(null);
      setIsScreenSharing(false);
    }
  }, [room, localVideoTrack, setIsScreenSharing, setScreenShareTrack]);

  //sweetalert closing on any alert is opened
  const closeSwal = () => {
    var swalObject = swal.getState();
    if (swalObject.isOpen) {
      swal.close();
    }
  };

  //disconnect from room instance
  const disconnectCurrentClient = (room) => {
    if (room) room.disconnect();
  };

  const getSmallWindow = (room) => {
    if (room === null) return [];
    let smallList = [room.localParticipant];
    if (room.participants.size) {
      for (let value of room.participants.values()) {
        if (value.identity === "receiver") {
          smallList.push(value);
        }
      }
    }
    return smallList;
  };

  const getBigWindow = (room) => {
    if (room === null) return [];
    let searchIdentity = "";
    if (room.localParticipant.identity === "doctor") {
      searchIdentity = "patient";
    } else {
      searchIdentity = "doctor";
    }
    let bigList = [];
    if (room.participants.size) {
      for (let value of room.participants.values()) {
        if (value.identity === searchIdentity) {
          bigList.push(value);
        }
      }
    }
    return bigList;
  };

  //disconnects the chat on page refresh
  useEffect(() => {
    const isRoomEmpty =
      room && Object.keys(room).length === 0 && room.constructor === Object;

    const isVideoCallStatus =
      (typeof isVideoCall === "string" && isVideoCall === "false") ||
      (typeof isVideoCall === "boolean" && isVideoCall === false);

    if (isVideoCallStatus && room && !isRoomEmpty) {
      setVideo(isVideoCall);
      muteVideo();
    }
    // if (isVideoCall !== undefined) {
    //   setVideo(isVideoCall);
    // }

    var endDateTime1;
    if (endDateTime === null) {
      endDateTime1 = moment(appointment.appointment_end_date_time).format();
    } else {
      clearTimeout(endMeetingTimer);
      endDateTime1 = moment(endDateTime).format();
    }

    var currentDateTime = moment();
    var timeDifference = Math.abs(
      currentDateTime.diff(endDateTime1, "milliseconds")
    );

    setEndMeetingTimer(
      setTimeout(() => {
        endSession(roomClosed, true);
      }, timeDifference)
    );
    return () => {
      closeSwal();
      clearTimeout(endMeetingTimer);
      setEndMeetingTimer(null);
      disconnectCurrentClient(room); // this needs to be fired only once when component onmounts but clearing effect is done every time videoProgress changes
    };
  }, [room, endDateTime]);

  //Room Creation and participant connection and disconnection handled
  useEffect(() => {
    const participantConnected = (participant) => {
      closeSwal();
      setParticipantStatus("Connected");
      setParticipants((prevParticipants) => [...prevParticipants, participant]);
    };

    const networkQualityChanged = (level) => {
      let statusText = "";
      if (level <= 2) {
        statusText =
          "Your network has low bandwidth which is causing poor call quality";
      } else {
        if (!isVideoOpen) {
          statusText =
            "Your Network Quality is high enough to switch back to the video call by unmuting your video";
        }
      }
      setNetworkStatusText(statusText);
      setNetworkStatus(level);
      displayNetworkText(statusText);
    };
    const participantDisconnected = (participant) => {
      setParticipants((prevParticipants) =>
        prevParticipants.filter((p) => p !== participant)
      );
    };

    const participantReconnected = (participant) => {
      setParticipantStatus("Connected");
      closeSwal();
    };

    const participantReconnecting = (participant) => {
      swal("Alert", participantStatus, {
        button: true,
        closeOnClickOutside: false,
      });
      setParticipantStatus("Reconnecting...");
    };

    const selfReconnecting = () => {
      setSelfStatus("Reconnecting");
      swal("Alert", "Reconnecting...", {
        button: false,
        closeOnClickOutside: false,
      });
    };

    const selfReconnected = (error) => {
      closeSwal();
      setSelfStatus("Connected");
    };

    const participantTrackDisabled = (track, participant) => {
      if (track.kind == "audio") {
        setParticipantAudioTrackStatus(false);
      } else if (track.kind == "video") {
        setParticipantVideoTrackStatus(false);
      }
    };

    const participantTrackEnabled = (track, participant) => {
      if (track.kind == "audio") {
        setParticipantAudioTrackStatus(true);
      } else if (track.kind == "video") {
        setParticipantVideoTrackStatus(true);
      }
    };

    const disconnected = (room, error) => {
      if (error) {
        if (error.message == "Room completed") {
          setRoomStatus(false);
          room.disconnect();
          endSession(roomClosed, true, true);
        } else {
          setSelfStatus("Disconnected");
          swal({
            // title: "Oops!",
            text: "Your network has low bandwidth which is causing poor call quality.",
            type: "failed",
          }).then(function () {
            window.location.reload();
          });
        }
      }
    };

    Video.connect(token, {
      name: roomName,
      networkQuality: {
        local: 1,
        remote: 1,
      },
      bandwidthProfile: {
        video: {
          mode: "grid",
          trackSwitchOffMode: "disabled",
          maxSubscriptionBitrate: 0,
          renderDimensions: {
            high: { width: 1280, height: 780 },
            standard: { width: 640, height: 480 },
            low: { width: 176, height: 144 },
          },
        },
      },
    })
      .then((room) => {
        setRoom(room);
        setRoomId(room.sid);
        if (room.participants.size === 0) {
          swal("Alert", participantStatus, {
            button: true,
            closeOnClickOutside: false,
          });
        }
        room.on("participantConnected", participantConnected);
        room.on("participantDisconnected", participantDisconnected);
        room.on("participantReconnected", participantReconnected);
        room.on("participantReconnecting", participantReconnecting);
        room.on("reconnecting", selfReconnecting);
        room.on("reconnected", selfReconnected);
        room.on("disconnected", disconnected);
        room.on("trackDisabled", participantTrackDisabled);
        room.on("trackEnabled", participantTrackEnabled);
        room.participants.forEach(participantConnected);
        room.localParticipant.on(
          "networkQualityLevelChanged",
          networkQualityChanged
        );
      })
      .catch((error) => {
        console.log("error while connecting --->", error);
        logger.error("error while connecting --->" + JSON.stringify(error));

        if (error.name == "NotAllowedError") {
          setParticipantStatus("Permission denied by the user");
          swal({
            title: "Permissions Denied",
            button: true,
            closeOnClickOutside: false,
            text: "connect2clinic requires access to your camera and microphone. Click the camera blocked icon in your browser.",
            icon: "error",
          });
        } else {
          setParticipantStatus("Browser Error");
          swal({
            // title: error.code,
            button: true,
            closeOnClickOutside: false,
            text:
              Error[error.code] ||
              "We are facing some issue. Wait for sometime and if issue still persists please reach out to our customer support team for further assistance",
            icon: "error",
          });
        }
      });

    return () => {
      setRoom((currentRoom) => {
        if (currentRoom && currentRoom.localParticipant.state === "connected") {
          currentRoom.localParticipant.tracks.forEach(function (
            trackPublication
          ) {
            trackPublication.track.stop();
          });
          currentRoom.disconnect();
          return null;
        } else {
          return currentRoom;
        }
      });
    };
  }, [roomName, token]);

  const remoteParticipants = getBigWindow(room).map((participant) => (
    <Participant
      key={participant.sid}
      participant={participant}
      muteAudio={muteAudio}
      muteVideo={muteVideo}
      screenShare={screenShare}
      roomClosed={roomClosed}
      isScreenSharing={isScreenSharing}
      endSession={endSession}
      audioStatus={isAudioOpen}
      videoStatus={isVideoOpen}
      roomId={roomId}
      room={room}
      participantStatus={participantStatus}
      appointment={appointment}
      isAudioCall={isAudioCall}
      isVideoCall={isVideoCall}
      participantVideoTrackStatus={participantVideoTrackStatus}
      participantAudioTrackStatus={participantAudioTrackStatus}
      handleOnHold={handleOnHold}
    />
  ));

  const localParticipants = getSmallWindow(room).map((participant) => (
    <Participant
      key={participant.sid}
      participant={participant}
      remote={true}
    />
  ));

  var participantConnectedClass =
    remoteParticipants.length == 0 ? "not-connected" : "";
  var participantStatusVisible =
    participantStatus === "Waiting for your participant to connect..." ||
    participantStatus === "Permission denied by the user" ||
    participantStatus === "Browser Error"
      ? ""
      : "hidden";
  var toolBarInvisible =
    participantStatus === "Permission denied by the user" ||
    participantStatus === "Browser Error"
      ? ""
      : participantStatusVisible;

  toolBarInvisible =
    participantStatus === "Connected" ? "" : participantStatusVisible;

  return (
    <div>
      <div className="col-sm-6 col-md-12 connection-string">
        <span className="connection-sts">Status: </span>
        <span className={"connection " + selfStatus}>{selfStatus}</span>
      </div>
      <div className="col-sm-6 col-md-12 network-indicator">
        <span className="network-sts">Network: </span>
        {[0, 1, 2, 3, 4].map((level) => (
          <div
            className="network-bar"
            key={level}
            style={{
              height: `${4 * (level + 1)}px`,
              background:
                networkStatus > level
                  ? networkStatus < 3
                    ? "#f00"
                    : networkStatus < 4
                    ? "#ff0"
                    : "#0c0"
                  : "#040",
            }}
          />
        ))}
      </div>
      <div
        className={`col-sm-6 col-md-12 patient-status ${participantStatusVisible}`}
      >
        <span className={"patient-connection"}>{participantStatus}</span>
      </div>
      <div className="room">
        <div className="client-video">{remoteParticipants}</div>
        <div className={`doctor-toolbar ${toolBarInvisible}`}>
          {remoteParticipants.length == 0 ? (
            <Toolbar
              muteVideo={muteVideo}
              muteAudio={muteAudio}
              endSession={endSession}
              screenShare={screenShare}
              audioStatus={isAudioOpen}
              videoStatus={isVideoOpen}
              isAudioCall={isAudioCall}
              isVideoCall={isVideoCall}
              isScreenSharing={isScreenSharing}
              handleOnHold={handleOnHold}
            />
          ) : (
            ""
          )}
        </div>
        <div className={"self-video " + participantConnectedClass}>
          {localParticipants}
        </div>
      </div>
    </div>
  );
};

export default Player;
